/**
 * 「我的设备」（ADR-0016）的纯逻辑：列表的顺序、每台下面那行小字、找回提醒说什么。界面在 devicesui.ts。
 */
import { daysBetween, localDateOf, type DeviceDto } from '@star-sea/shared';

/** 这台最前；其余按最近来过的在前，没来过的按加入的先后放最后 */
export function sortDevices(list: readonly DeviceDto[]): DeviceDto[] {
  const seen = (d: DeviceDto) => d.lastSeenAt ?? d.createdAt;
  return [...list].sort((a, b) => {
    if (a.current !== b.current) return a.current ? -1 : 1;
    const x = seen(a), y = seen(b);
    return x === y ? (a.id < b.id ? -1 : 1) : x < y ? 1 : -1;
  });
}

/** 「刚刚来过」「3 小时前来过」「昨天来过」「5 天前来过」「9 月 20 日来过」；按这台设备的时间 */
export function seenText(at: string | undefined, now: number): string {
  if (!at) return '还没来过';
  const t = new Date(at);
  const ms = now - t.getTime();
  if (ms < 10 * 60_000) return '刚刚来过';
  const days = daysBetween(localDateOf(t), localDateOf(now));
  if (days <= 0) return ms < 3_600_000 ? `${Math.floor(ms / 60_000)} 分钟前来过` : `${Math.floor(ms / 3_600_000)} 小时前来过`;
  if (days === 1) return '昨天来过';
  if (days < 7) return `${days} 天前来过`;
  return `${dayText(t, now)}来过`;
}

/** 「9 月 20 日」，不是今年的带上年份 */
function dayText(t: Date, now: number): string {
  const same = t.getFullYear() === new Date(now).getFullYear();
  return `${same ? '' : `${t.getFullYear()} 年 `}${t.getMonth() + 1} 月 ${t.getDate()} 日`;
}

/** 怎么加入的：Ta 帮忙生成的找回码、服务器上生成的找回码；普通加入的不说 */
export function recoveredText(d: DeviceDto, nameOf: (memberId: string) => string): string | null {
  if (!d.recovered) return null;
  return d.recoveredBy ? `用 ${nameOf(d.recoveredBy)} 生成的找回码加入` : '用服务器上生成的找回码加入';
}

/** 设备名下面那行小字：「这台 · 10 月 2 日加入」「3 天前来过 · 9 月 20 日加入 · 用 小满 生成的找回码加入」 */
export function deviceMeta(d: DeviceDto, o: { now: number; nameOf(memberId: string): string }): string {
  return [d.current ? '这台' : seenText(d.lastSeenAt, o.now), `${dayText(new Date(d.createdAt), o.now)}加入`, recoveredText(d, o.nameOf)].filter(Boolean).join(' · ');
}

/** 有一台设备用找回码加入了：你所有的设备都收到这句（ADR-0016），不是你加的就去移除它 */
export function recoveredNotice(d: DeviceDto, nameOf: (memberId: string) => string): string {
  const who = d.recoveredBy ? ` ${nameOf(d.recoveredBy)} 帮你生成的找回码` : '服务器上生成的找回码';
  return `「${d.label}」用${who}加入了你的星海。不是你加的话，到「我的设备」里移除它。`;
}

/** 改名的输入：去掉首尾空白；空的不行 */
export function cleanLabel(raw: string, max: number): string | null {
  const v = raw.trim();
  return v && [...v].length <= max ? v : null;
}
